import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "@/lib/helpers/dayjs";

import { AcknowledgedPolicyEnum } from "@/core/constants";
import { DEFAULT_TOAST_TIMEOUT } from "@/lib/constants/delay";
import { Button2 } from "@/ui2/atoms/Button";
import { Card2 } from "@/ui2/atoms/Card";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Link2 } from "@/ui2/atoms/Link";
import { createAcknowledgementV2 } from "@/utils/acknowledgementUtils";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { selectPatientCardInfo } from "@/redux/selectors/patientCardInfoSelectors";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { showToast } from "@/providers/ToastProvider";
import { tryCatch } from "@/lib/helpers/tryCatch";

import { api } from "../../../APIRequests";
import { hideLoader, showLoader } from "../../../redux/actions/loader";
import { CreditCardAgreementScrollableTextModalHOC } from "../../../component-library/hocs/CreditCardAgreementScrollableTextModalHOC";

import {
  displayElavonCreditCardModal,
  parsePaymentResponse,
} from "../../../utils/PaymentProvider/elavon";
import {
  fetchPatientPaywall,
  putPatientCardInfo,
  updatePatientPaywallInfo,
} from "../../../redux/actions/patientCardInfo";
import { profileFetchSuccess } from "../../../redux/actions/auth";
import Captcha from "../Captcha/Captcha";
import {
  SUPPORT_EMAIL_HREF,
  SUPPORT_NUMBER,
  SUPPORT_NUMBER_HREF,
} from "../../../constants/aptihealth";

import "./styles.scss";

const PAYMENT_ERROR_MESSAGE = "Your card couldn't be added. Please try again.";
const WORKFLOW = "PAYWALL";

export function PaymentPaywall({ onPaywallCompleted }) {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const { isSoftPaywall, isPaymentUpdateRequired, lastFailedChargeDate } =
    useSelector(selectPatientCardInfo);

  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaVerificationToken, setCaptchaVerificationToken] = useState(false);

  const { first_name, last_name, username, acknowledgements } = profile;

  const onBackendError = () => {
    showToast(backendErrorToast);
    onPaywallCompleted();
  };

  const onCancelled = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);
  const onDeclined = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);
  const onError = () => setErrorMessage(PAYMENT_ERROR_MESSAGE);

  const onApproval = (response) => {
    dispatch(
      putPatientCardInfo({
        username: username,
        ...parsePaymentResponse(response),
        onErrorCallback: onBackendError,
      }),
    );

    onPaywallCompleted();
    showToast(successToast);
  };

  const closeModal = () => {
    setShowModal(false);
    setCaptchaVerificationToken(null);
  };

  const handleElavonCreditCardModalException = (e) => {
    showToast(errorToast);
    // Allow user to temporarily progress if error is outside their control
    if (!e?.code || e?.code >= 500) {
      showToast(errorToast);

      onPaywallCompleted();
      dispatch(updatePatientPaywallInfo({ showPaywall: false }));
    }

    throw e;
  };

  const onCreditCardTncSubmit = async () => {
    dispatch(showLoader());

    const acknowledgementsData = {
      username,
      acknowledgements: {
        ...acknowledgements,
        [AcknowledgedPolicyEnum.CREDIT_CARD_AUTHORIZATION_POLICY_NAME]: createAcknowledgementV2({
          consentResponse: true,
        }),
      },
    };

    dispatch(hideLoader());
    dispatch(profileFetchSuccess({ ...profile, ...acknowledgementsData }));

    const [_profileRes, profileError] = await tryCatch(
      api.patient.update_profile({ data: acknowledgementsData }),
    );

    if (profileError) {
      showToast(errorToast);
      return;
    }

    closeModal();

    const [_elavonRes, elavonError] = await tryCatch(
      displayElavonCreditCardModal(
        dispatch,
        username,
        first_name,
        last_name,
        () => {}, // setErrorMessage
        WORKFLOW,
        onError,
        onCancelled,
        onDeclined,
        onApproval,
        false,
        captchaVerificationToken,
      ),
    );

    if (elavonError) handleElavonCreditCardModalException(elavonError);

    setCaptchaVerificationToken(null);
  };

  useEffect(() => {
    if (isSoftPaywall === undefined) {
      dispatch(fetchPatientPaywall());
    }

    async function updateProfile() {
      const [_response, error] = await tryCatch(
        api.patient.update_profile({
          data: { ...profile, soft_paywall: Date.now() },
        }),
      );

      if (error) showToast(errorToast);
    }

    if (isSoftPaywall) updateProfile();
  }, [dispatch, isSoftPaywall, profile]);

  const captchaComponent = <Captcha setCaptchaVerificationToken={setCaptchaVerificationToken} />;

  return (
    <>
      <CreditCardAgreementScrollableTextModalHOC
        show={showModal}
        onCancel={closeModal}
        onSubmit={onCreditCardTncSubmit}
        captchaComponent={captchaComponent}
        captchaCompleted={captchaVerificationToken}
      />

      <Card2
        className="tw:p-12 tw:flex-col tw:gap-8 tw:mx-auto tw:lg:max-w-146"
        {...getAutomationDataAttr("payment-paywall-card")}
      >
        <Heading2 as="h1" size={4} className="tw:text-center">
          {isPaymentUpdateRequired ? "Update payment method" : "Add a payment method"}
        </Heading2>

        {errorMessage && (
          <p className="tw:p-3 tw:pb-2 tw:rounded-lg tw:border-stroke-error-primary tw:border tw:bg-fill-error-tertiary tw:text-text-error-primary">
            <b>{errorMessage}</b>
          </p>
        )}

        {isPaymentUpdateRequired && (
          <>
            {lastFailedChargeDate && (
              <p className="body-md">
                We were unable to charge your card on file on
                <time className="tw:ml-0.5ch" dateTime={dayjs(lastFailedChargeDate).toISOString()}>
                  {dayjs(lastFailedChargeDate).format("MMM D, YYYY")}
                </time>
                .
              </p>
            )}

            <p className="body-md">
              If your card on file is disabled or expired, please update your payment information.
              If your card on file is still valid and you've resolved processing issues with your
              bank, please add the card again.
            </p>
          </>
        )}

        {!isPaymentUpdateRequired && (
          <p className="body-md">
            Based on your benefits, you may have additional costs associated with your care. Adding
            a payment method to your account is required to connect with a provider.
          </p>
        )}

        <Button2
          className="tw:min-w-50 tw:!mx-auto"
          onPress={() => {
            window.grecaptcha.reset();
            setShowModal(true);
          }}
          size="md"
        >
          {isPaymentUpdateRequired ? "Update Card" : "Add Card"}
        </Button2>

        {isSoftPaywall && (
          <p className="body-sm" style={{ color: "var(--brand-gray-600) !important" }}>
            Don’t have your card handy?
            <Link2
              className="tw:mx-0.5ch"
              onClick={onPaywallCompleted}
              {...getAutomationDataAttr("payment-paywall-skip")}
            >
              Skip this step.
            </Link2>
            Next time you sign in, your account will be locked until you add a payment method.
          </p>
        )}

        <hr className="tw:!my-0" />

        <p
          className="body-xs tw:!text-text-tertiary"
          style={{ color: "var(--brand-gray-600) !important" }}
        >
          <b
            className="tw:block"
            // reboot workaround
            style={{ color: "var(--brand-gray-600) !important" }}
          >
            Crisis Support
          </b>
          If you are in a life-threatening situation, call or text the
          <Link2 className="tw:mx-0.5ch" href="https://988lifeline.org" isExternal>
            Suicide and Crisis Lifeline
          </Link2>
          at <Link2 href="tel:988">988</Link2>.
        </p>

        <p className="body-xs" style={{ color: "var(--brand-gray-600) !important" }}>
          <b className="tw:block" style={{ color: "var(--brand-gray-600) !important" }}>
            Billing Support
          </b>
          For urgent support from the aptihealth team, please reach out to our front office at
          <Link2 className="tw:mx-0.5ch tw:whitespace-nowrap" href={SUPPORT_NUMBER_HREF}>
            {SUPPORT_NUMBER}
          </Link2>
          . For all other credit card or payment related questions, contact our billing team at
          <Link2 className="tw:mx-0.5ch" href={SUPPORT_EMAIL_HREF}>
            support@aptihealth.com
          </Link2>
          .
        </p>
      </Card2>
    </>
  );
}

const successToast = [
  { heading: "New card added.", variant: "success" },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];

const backendErrorToast = [
  {
    heading: "Error adding card on file: Next time you sign in, we'll ask you to try again.",
    variant: "error",
  },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];

const errorToast = [
  {
    heading:
      "Unexpected error adding card on file: Next time you sign in, we'll ask you to try again.",
    variant: "error",
  },
  { timeout: DEFAULT_TOAST_TIMEOUT },
];
